import { homedir } from 'node:os';
import { join } from 'node:path';
import { existsSync, readFileSync, renameSync } from 'node:fs';
import type { SqliteDatabase } from './sqlite/database.js';
import { openOfflineDatabase } from './sqlite/database.js';
import { OfflineSyncOrchestrator } from './sync-orchestrator.js';
import type { SyncHttpClient } from './sync-engine.js';
import type { OfflineAuthCrypto } from './auth-crypto.js';
import type { OfflinePosPersistedState } from './types.js';
import { OFFLINE_POS_STORAGE_KEY } from './types.js';

export function getDefaultOfflineDbPath(): string {
  return join(homedir(), '.incashflow-pos', 'offline.db');
}

export function getLegacyJsonCachePath(): string {
  return join(homedir(), '.incashflow-pos', 'offline-cache.json');
}

export function migrateJsonCacheToSqliteIfNeeded(db: SqliteDatabase): boolean {
  const jsonPath = getLegacyJsonCachePath();
  const migratedPath = `${jsonPath}.migrated`;
  if (!existsSync(jsonPath) || existsSync(migratedPath)) {
    return false;
  }
  try {
    const raw = readFileSync(jsonPath, 'utf8');
    const outer = JSON.parse(raw) as Record<string, string>;
    const inner = outer[OFFLINE_POS_STORAGE_KEY];
    if (!inner) return false;
    const state = JSON.parse(inner) as OfflinePosPersistedState;
    OfflineSyncOrchestrator.importJsonV2(db, state);
    renameSync(jsonPath, migratedPath);
    return true;
  } catch {
    return false;
  }
}

export function createOfflineOrchestrator(options: {
  dbPath?: string;
  http: SyncHttpClient;
  healthPath?: string;
  authCrypto?: OfflineAuthCrypto;
}): OfflineSyncOrchestrator {
  const db = openOfflineDatabase(options.dbPath ?? getDefaultOfflineDbPath());
  migrateJsonCacheToSqliteIfNeeded(db);
  return new OfflineSyncOrchestrator({
    db,
    http: options.http,
    healthPath: options.healthPath,
    authCrypto: options.authCrypto,
  });
}
